import {
	Autocomplete,
	Box,
	Button,
	Grid2,
	MenuItem,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { isValidPhoneNumber } from "libphonenumber-js";
import MuiPhoneNumber from "mui-phone-number";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	createCompanyUser,
	getCompanyUsers,
	updateCompanyUser,
} from "../../../../../../../export/gat-admin";
import LoadingButton from "../../../../../../design/wilya-loading-button";
import { deCamelCase } from "../../../../../../utils/helpers";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import { getUserRoleLabel } from "../../role-labels";
import useCompanyUserController from "../controllers/use-company-user-controller";
import WorkcenterTransfer from "./workcenter-transfer";
//import isEmail from "validator/lib/isEmail";

const isEmail = (email: string | null | undefined): boolean => {
	if (!email) return false;

	// Basic email regex pattern
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

interface CompanyUserDialogProps {
	creatingNewCompanyUser: any;
	companyUserData: any;
	setCompanyUserData: any;
	onClose: any;
}

const CompanyUserDialog: React.FC<CompanyUserDialogProps> = ({
	creatingNewCompanyUser,
	companyUserData,
	setCompanyUserData,
	onClose,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [initialCompanyUserData, setInitialCompanyUserData] = useState({});
	const [activeStep, setActiveStep] = useState(0);
	const steps = [t("users_step-user-details"), t("users_step-associated-workcenters")];

	const [
		{
			createNewCompanyUserStatus,
			updateCompanyUserStatus,
			openCompanyUserDialog,
			allCompanyUserRoles,
			allCompanyWorkCenters,
		},
		{
			checkRoleId,
			getUserRole,
			workCenterIncluded,
			addWorkCenterFromCompany,
			removeWorkCenterFromCompany,
		},
	] = useCompanyUserController({ companyUserData, setCompanyUserData });

	const [phoneNumberError, setPhoneNumberError] = useState("");

	const roles = useSelector(
		(state: any) => state.user?.userData?.userRoles
	);

	const companyUsers = useSelector(
		(state: any) => state.gatAdminConfiguration.companyUsers
	);

	useEffect(() => {
		try {
			// Only while editing we will get email in companyUserData
			if (Object.prototype.hasOwnProperty.call(companyUserData, "email")) {
				if (
					JSON.stringify(initialCompanyUserData) === "{}" &&
					JSON.stringify(companyUserData) !== "{}"
				) {
					setInitialCompanyUserData(companyUserData);
				}
			}
		} catch (e) {
			// Do nothing
		}
	}, [companyUserData, initialCompanyUserData]);

	const closeCompanyUserDialog = useCallback(() => {
		setActiveStep(0);
		setPhoneNumberError("");
		setInitialCompanyUserData({});
		onClose();
	}, [onClose]);

	useEffect(() => {
		const isSuccessful =
			createNewCompanyUserStatus === "fulfilled" ||
			updateCompanyUserStatus === "fulfilled";
		const hasCompanyId =
			initialCompanyUserData && "companyId" in initialCompanyUserData;

		if (isSuccessful && hasCompanyId) {
			dispatch(getCompanyUsers(initialCompanyUserData.companyId as number));
			closeCompanyUserDialog();
		}
	}, [
		createNewCompanyUserStatus,
		updateCompanyUserStatus,
		initialCompanyUserData,
		closeCompanyUserDialog,
		dispatch,
	]);
	// console.log(
	// 	"CompanyUserDialog",
	// 	companyUserData,
	// 	"initialCompanyUserData",
	// 	initialCompanyUserData
	// );
	return (
		<Dialog
			onClose={() => closeCompanyUserDialog()}
			aria-labelledby='add-new-company-user'
			open={openCompanyUserDialog}
			maxWidth={false}
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<DialogTitle
				id='company-dialog-title'
				sx={{
					width: "900px",
				}}
			>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontSize: "20px",
						fontWeight: 500,
						color: "rgba(0, 0, 0, 0.87)",
						paddingTop: "29px",
						paddingLeft: "21px",
						paddingBottom: "9px",
						paddingRight: "21px",
					}}
				>
					{creatingNewCompanyUser ? t("users_add-user") : t("users_edit-user")}
				</Typography>
			</DialogTitle>
			<Divider />
			<DialogContent>
				{/*<Grid container sx={{*/}
				{/*    paddingTop: "12px",*/}
				{/*    paddingLeft: "21px",*/}
				{/*    paddingRight: "21px",*/}
				{/*    paddingBottom: "16px"*/}
				{/*}}>*/}
				{/*    <Grid item xs={8}>*/}
				{/*        <TextField*/}
				{/*            label='Username'*/}
				{/*            value={companyUserData.username ?? ""}*/}
				{/*            onChange={e => setCompanyUserData({...companyUserData, username: e.target.value})}*/}
				{/*            variant='filled'*/}
				{/*            fullWidth*/}
				{/*        />*/}
				{/*    </Grid>*/}
				{/*</Grid>*/}
				<Stepper activeStep={activeStep} alternativeLabel>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
				{activeStep === 0 && (
					<>
						<Grid2
							container
							sx={{
								paddingTop: "12px",
								paddingLeft: "21px",
								paddingRight: "21px",
								paddingBottom: "16px",
							}}
						>
							<Grid2  size={{ xs: 6 }} pr={0.75}>
								<TextField
									label={t("users_first-name")}
									inputProps={{ maxLength: 50 }}
									value={companyUserData.firstName ?? ""}
									onChange={(e) =>
										setCompanyUserData({
											...companyUserData,
											firstName: e.target.value,
										})
									}
									variant='filled'
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }} pl={0.75}>
								<TextField
									label={t("users_last-name")}
									inputProps={{ maxLength: 50 }}
									value={companyUserData.lastName ?? ""}
									onChange={(e) =>
										setCompanyUserData({
											...companyUserData,
											lastName: e.target.value,
										})
									}
									variant='filled'
									fullWidth
								/>
							</Grid2>
						</Grid2>
						<Grid2
							container
							sx={{
								paddingTop: "12px",
								paddingLeft: "21px",
								paddingRight: "21px",
								paddingBottom: "16px",
							}}
						>
							<Grid2 size={{ xs: 6 }} pr={0.75}>
								<TextField
									label={t("users_email-address")}
									disabled={!creatingNewCompanyUser}
									value={companyUserData.email ?? ""}
									inputProps={{ maxLength: 255 }}
									onChange={(e) =>
										setCompanyUserData({
											...companyUserData,
											email: e.target.value.trim(),
										})
									}
									variant='filled'
									fullWidth
								/>
							</Grid2>

							<Grid2 size={{ xs: 6 }} pl={0.75}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										justifyContent: "center",
									}}
								>
									<TextField
										label={t("users_user-role")}
										value={getUserRole && getUserRole(companyUserData.roleId)}
										variant='filled'
										fullWidth
										select
										onChange={(e) => {
											const roleId = allCompanyUserRoles.filter(
												(role: { name: string }) =>
													deCamelCase(role.name) === e.target.value
											)[0].id;

											setCompanyUserData({
												...companyUserData,
												roleId: roleId,
											});
										}}
									>
										{allCompanyUserRoles &&
											allCompanyUserRoles.map(
												(role: { id: number; name: string }) => (
													<MenuItem
														key={`company-user-role-${role.id}`}
														value={deCamelCase(role.name)}
													>
														{getUserRoleLabel(role.name, t)}
													</MenuItem>
												)
											)}
									</TextField>
								</Box>
							</Grid2>
						</Grid2>

						<Grid2
							container
							sx={{
								paddingTop: "16px",
								paddingLeft: "21px",
								paddingRight: "21px",
								paddingBottom: "16px",
							}}
						>
							<Grid2 size={{ xs: 6 }} pl={0.75}>
								<MuiPhoneNumber
									defaultCountry={"us"}
									onlyCountries={["us", "mx", "in", "om", "gb", "br", "ca", "au"]}
									label={t("users_phone-number-optional")}
									disableAreaCodes={true}
									autoFormat={false}
									value={companyUserData.phoneNumber ?? ""}
									onChange={(phone) => {
										const phoneStr = phone as string;
										if (
											isValidPhoneNumber(phoneStr) ||
											phoneStr.replace(/\D/g, "").length <= 3
										) {
											setPhoneNumberError("");
										} else {
											setPhoneNumberError(t("users_invalid-phone-number"));
										}
										setCompanyUserData({
											...companyUserData,
											phoneNumber: phone,
										});
									}}
									error={phoneNumberError !== ""}
									helperText={phoneNumberError}
									variant='filled'
									fullWidth
								/>
							</Grid2>
							<Grid2 size={{ xs: 6 }} pl={0.75}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										justifyContent: "center",
									}}
								>
									<Autocomplete
										options={
											Array.isArray(companyUsers)
												? companyUsers.filter(
														(user: any) =>
															user.email !== companyUserData.email
											)
												: []
										}
										getOptionLabel={(option: any) =>
											`${option.firstName} ${option.lastName}`
										}
										getOptionKey={(option: any) => option.id}
										getOptionDisabled={(option: any) => !option.isActive}
										value={
											Array.isArray(companyUsers) ? companyUsers?.find(
												(user: any) => user.id === companyUserData.managerId
											) ?? null : null
										}
										onChange={(_, newValue) => {
											setCompanyUserData({
												...companyUserData,
												managerId: newValue?.id ?? null,
											});
										}}
										renderOption={(props, option) => (
											<li
												{...props}
												style={{
													display: "flex",
													justifyContent: "space-between",
												}}
											>
												<span>
													{option.firstName} {option.lastName}
												</span>
												{!option.isActive && (
													<Box
																sx={{
																	backgroundColor: "#F9DEDC",
																	borderRadius: "4px",
																	opacity: 1,
																}}
															>
																<Typography
																	sx={{
																		color: "#E44B48",
																		padding: "2px 4px",
																		fontSize: "0.8rem",
																		fontWeight: "500",
																		opacity: 1,
																	}}
																>
																	{t("users_inactive")}
																</Typography>
															</Box>
												)}
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t("users_manager-optional")}
												variant='filled'
											/>
										)}
										fullWidth
										isOptionEqualToValue={(option: any, value: any) =>
											option.id === value.id
										}
									/>
								</Box>
							</Grid2>
						</Grid2>
						<Grid2
							container
							sx={{
								paddingTop: "16px",
								paddingLeft: "21px",
								paddingRight: "21px",
								paddingBottom: "16px",
							}}
						>
							<Grid2 size={{ xs: 12 }}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "flex-start",
									}}
								>
									<Switch
										checked={companyUserData.emailNotificationsEnabled ?? true}
										onChange={(e) =>
											setCompanyUserData({
												...companyUserData,
												emailNotificationsEnabled: e.target.checked,
											})
										}
									/>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontWeight: 400,
											fontSize: "16px",
											color: "rgba(0, 0, 0, 0.87)",
										}}
									>
										{t("users_email-notifications")}
									</Typography>
								</Box>
							</Grid2>
						</Grid2>
					</>
				)}
				{activeStep === 1 && (
					<WorkcenterTransfer
						allCompanyWorkCenters={allCompanyWorkCenters}
						companyUserData={initialCompanyUserData}
						setCompanyUserData={setCompanyUserData}
					/>
				)}
			</DialogContent>
			<Divider />

			<Box
				sx={{
					display: "flex",
					//flexDirection: "row",
					justifyContent: "space-between",
					marginTop: "16px",
					marginBottom: "16px",
					marginRight: "21px",
					marginLeft: "21px",
				}}
			>
				<Box>
					<Button
						disabled={activeStep === 0}
						onClick={() =>
							setActiveStep((prevActiveStep) => prevActiveStep - 1)
						}
					>
						{t("users_back")}
					</Button>
				</Box>
				<Box>
					<Button
						variant='text'
						onClick={() => {
							setActiveStep(0);
							closeCompanyUserDialog();
						}}
						sx={{ marginRight: "16px" }}
					>
						{t("common_cancel")}
					</Button>
					{/*First Name, Last Name, Email, at least a Work center and Phone number required */}
					{activeStep === steps.length - 1 ? (
						<LoadingButton
							variant='contained'
							disabled={
								companyUserData?.workCenterIds === "" ||
								JSON.stringify(initialCompanyUserData) ===
									JSON.stringify(companyUserData) ||
								(!!companyUserData.phoneNumber &&
									companyUserData.phoneNumber.replace(/\D/g, "").length > 3 &&
									!isValidPhoneNumber(companyUserData.phoneNumber))
							}
							sx={{ marginLeft: "16px" }}
							loading={
								createNewCompanyUserStatus === "pending" ||
								updateCompanyUserStatus === "pending"
							}
							onClick={() => {
								// Trim extra spaces
								companyUserData = {
									...companyUserData,
									firstName: companyUserData.firstName.trim(),
									lastName: companyUserData.lastName.trim(),
								};

								if (creatingNewCompanyUser) {
									dispatch(createCompanyUser(companyUserData));
								} else {
									dispatch(updateCompanyUser(companyUserData));
								}
							}}
							loadingIndicator={
								<CircularProgress
									size={16}
									sx={{ color: "rgba(0, 0, 0, 0.38)" }}
								/>
							}
						>
							{creatingNewCompanyUser ? t("users_create") : t("users_update")}
						</LoadingButton>
					) : (
						<Button
							variant='contained'
							onClick={() =>
								setActiveStep((prevActiveStep) => prevActiveStep + 1)
							}
							disabled={
								!isEmail(companyUserData.email) ||
								companyUserData?.firstName === "" ||
								companyUserData?.lastName === "" ||
								(!!companyUserData.phoneNumber &&
									companyUserData.phoneNumber.replace(/\D/g, "").length > 3 &&
									!isValidPhoneNumber(companyUserData.phoneNumber))
							}
						>
							{t("users_next")}
						</Button>
					)}
				</Box>
			</Box>
		</Dialog>
	);
};

export default CompanyUserDialog;
